/**
 * Whether an entry would take a budget past its limit.
 *
 * Alongbookable.ts and lockdown.ts because it is the same kind of rule: a
 * question every write path has to answer identically. Bulk paths must apply every
 * guard the single-entry path does, and a budget that only the day form respects
 * is a budget the bulk fill spends.
 *
 * Ported from the previous system's TimesheetBudgetUsedValidator, keeping its
 * order and its comparisons:
 *
 *   - **Activity, then project, then customer.** The first refusal wins, so the
 *     message names the narrowest thing that is out of budget - which is the one
 *     somebody can act on.
 *   - **Money and time are separate budgets.** Either can refuse alone, and an
 *     entity can have one without the other.
 *   - **Only billable entries count.** Unbillable time is not spending.
 *   - **Strictly greater.** Landing exactly on the budget is allowed; the entry
 *     that goes one paisa over is not.
 *
 * There is nothing to migrate into this: every budget column in the source is
 * zero on all 57 customers, 60 projects and 117 activities. And the setting that
 * governs it - `timesheet.rules.allow_overbooking_budget` - was ON there, so the
 * refusal never once fired. It defaults to the same here, which means turning it
 * off is a deliberate act rather than a surprise.
 */
import { formatDuration } from './time';

/** What one entity's budget looks like, in the units they are stored in. */
export interface BudgetLimits {
  /** Money, minor units. Zero means no monetary budget. */
  budgetMinor: bigint;
  /** Time, seconds. Zero means no time budget. */
  timeBudgetSeconds: number;
  /** Null for a lifetime budget; 'month' for one that resets each month. */
  budgetType: 'month' | null;
}

/** What has already been spent against it, over the period the budget covers. */
export interface BudgetSpent {
  spentMinor: bigint;
  spentSeconds: number;
}

export interface BudgetCheck {
  /** Named in the refusal, so the message says which thing to change. */
  label: string;
  kind: 'activity' | 'project' | 'customer';
  limits: BudgetLimits;
  spent: BudgetSpent;
}

export interface BudgetDelta {
  /** What this write adds. On an edit, the CHANGE, which may be negative. */
  addedMinor: bigint;
  addedSeconds: number;
  /** An unbillable entry spends nothing, whatever its duration. */
  billable: boolean;
}

/**
 * The reason this entry is refused, or null.
 *
 * Checked in the previous system's order and phrased the way the rest of this
 * codebase phrases refusals: naming the thing and the figures, because "budget
 * exceeded" against a picker of sixty projects tells nobody which one to change.
 */
export function budgetRefusal(
  checks: readonly BudgetCheck[],
  delta: BudgetDelta,
  /** When true nothing is refused - the previous system's default, and ours. */
  allowOverbooking: boolean,
): string | null {
  if (allowOverbooking) return null;
  if (!delta.billable) return null;

  const order = { activity: 0, project: 1, customer: 2 } as const;
  const sorted = [...checks].sort((a, b) => order[a.kind] - order[b.kind]);

  for (const check of sorted) {
    const { limits, spent, label, kind } = check;
    const period = limits.budgetType === 'month' ? 'this month' : 'in total';

    // Money. Only when the entry actually bills something: a zero-rate entry
    // cannot exhaust a monetary budget, and refusing it would be baffling.
    if (limits.budgetMinor > 0n && delta.addedMinor > 0n) {
      const after = spent.spentMinor + delta.addedMinor;
      if (after > limits.budgetMinor) {
        return (
          `The ${kind} "${label}" has a budget of ${money(limits.budgetMinor)} ${period}, ` +
          `${money(spent.spentMinor)} of it already spent. This entry would take it to ` +
          `${money(after)}.`
        );
      }
    }

    // Time, independently of money.
    if (limits.timeBudgetSeconds > 0 && delta.addedSeconds > 0) {
      const after = spent.spentSeconds + delta.addedSeconds;
      if (after > limits.timeBudgetSeconds) {
        return (
          `The ${kind} "${label}" has a time budget of ` +
          `${formatDuration(limits.timeBudgetSeconds)} ${period}, ` +
          `${formatDuration(spent.spentSeconds)} of it already used. This entry would take ` +
          `it to ${formatDuration(after)}.`
        );
      }
    }
  }

  return null;
}

/** Minor units for a message. Display only - never fed back into arithmetic. */
function money(minor: bigint): string {
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

/**
 * How much of a budget is gone, as a percentage rounded to a whole number.
 *
 * For the screens rather than the rule. Returns null when there is no budget, so
 * a caller shows nothing instead of "0% of 0".
 */
export function budgetUsedPercent(limit: bigint | number, spent: bigint | number): number | null {
  const l = typeof limit === 'bigint' ? Number(limit) : limit;
  const s = typeof spent === 'bigint' ? Number(spent) : spent;
  if (l <= 0) return null;
  return Math.round((s / l) * 100);
}
